import { observable, computed, action } from 'mobx';
import i18next from 'i18next';

import { AppManager, ConnectionAppManager, Variables } from 'core/index';

import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { InitiativeModel, InitiativeApp } from 'apps/initiative/app';

import { ThreadForm } from 'apps/forum/threadForm/index';
import { ForumApp, ForumModel, ThreadsApp, ForumsApp } from 'apps/forum/app';
import { ForumQuery, ThreadsQuery, ForumsQuery } from 'apps/forum/gql';

export class ThreadConnManager extends ConnectionAppManager {
  static ArrayApp = ThreadsApp;
  @observable sortBy;
  @observable sortOptions;

  constructor(parent, init, options) {
    super(parent, init, options);
    this.initialBatchSize = init.initialBatchSize || 10;
    this.sortOptions = init.sortOptions || [
      ['recent', i18next.t('Recent')],
      ['likes', i18next.t('Likes')],
      ['comments', i18next.t('Comments')],
    ];
    this.sortBy = init.sortBy || 'recent';
    this.batchSize = init.batchSize || 10;
    this.whitelabels = init.whitelabels || [];
    this.notContains = init.notContains || [];
    this.variables = new Variables({
      sortBy: () => this.sortBy,
      batchSize: () => {
        if (this.loadedMore) {
          return this.batchSize;
        }
        return this.initialBatchSize;
      },
      cursor: () => this.endCursor,
      slug: () => init.slug,
      urlId: () => init.categoryId || null,
      whitelabels: () => this.whitelabels,
      notContains: () => this.notContains,
    });
    this.loadMoreQuery = init.loadMoreQuery || ThreadsQuery;
  }

  @action
  NewVariables() {
    this.Reset();
  }

  @action
  SetSort(sort) {
    this.sortBy = sort;
    this.Clear();
    this.loadMore();
  }
}

export class ForumAppManager extends AppManager {
  static registryName = 'ForumAppManager';
  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('forum', ForumApp);
    this.AppRef('initiative', InitiativeApp);
    this.threadsConn = new ThreadConnManager(this, {
      slug: init.params.get('initiative'),
      sortOptions: [],
    });

    this.variables = new Variables({
      slug: () => init.params.get('initiative'),
      threadCursor: () => this.threadsConn.endCursor,
      threadSortBy: () => this.threadsConn.sortBy,
      threadBatchSize: () => this.threadsConn.batchSize,
      forumId: () => this.forum.forumId,
    });
    this.variables.onChange(['slug'], this.NewVariables);
    clientStore.context.set('forumAppManager', this);
  }

  @computed
  get loading() {
    return this.queries.get('forum')?.loading;
  }

  @action
  NewVariables = () => {
    this.threadsConn.NewVariables();
    this.NewForumQuery();
  };

  @action
  NewForumQuery = () => {
    const q = new ForumQuery(this, {
      queryKey: 'forum',
      onSysErr: this.onError,
    });
    q.process(
      initiative => InitiativeModel.Load(initiative),
      this.InitiativeUpdate,
    );
    q.process(forum => ForumModel.Load(forum), this.ForumUpdate, {
      subPath: 'forum',
    });
    q.process(this.threadsConn.process, this.ThreadsUpdate, {
      subPath: 'forum.threads',
    });
    q.query();
  };

  @action
  InitiativeUpdate = model => {
    this.initiative = model;
  };

  @action
  ForumUpdate = model => {
    this.forum = model;
  };

  @action
  ThreadsUpdate = (threads, query) => {
    this.threadsConn.pushResultIfFirst(threads, query);
  };

  @action
  OpenThreadForm = () => {
    modalStore.SetModalComponent(ThreadForm, {
      parent: this,
      hideOnlyOnEsc: true,
    });
  };
}

export class ForumsConnManager extends ConnectionAppManager {
  static ArrayApp = ForumsApp;
  @observable sortBy;
  @observable sortOptions;

  constructor(parent, init) {
    super(parent, init);
    this.initialBatchSize =
      init.initialBatchSize || this.arrayApp.cardLayout.initialBatchSize;
    this.batchSize = init.batchSize || this.arrayApp.cardLayout.batchSize;
    this.sortOptions = init.sortOptions || [['recent', i18next.t('Recent')]];
    this.sortBy = init.sortBy || 'recent';
    this.variables = new Variables({
      sortBy: () => this.sortBy,
      batchSize: () => {
        if (this.loadedMore) {
          return this.batchSize;
        }
        return this.initialBatchSize;
      },
      cursor: () => this.endCursor,
      contains: () => this.contains,
    });
    this.loadMoreQuery = init.loadMoreQuery || ForumsQuery;
  }
}
